import { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { evaluateEntitlements } from '../entitlements/evaluate';

// Everything the account & billing page needs about the money side of an
// account, from ONE users/{uid} listener: what they're entitled to, the raw
// Stripe state behind it (renewal date, pending cancellation, customer id),
// and their credit buckets. useEntitlements and useCredits each open their own
// listener on this same document and each read only a slice of it, so the
// account page consumes this one instead of both and passes the result down.
const EMPTY = {
  entitlements: null,
  subscription: null,
  credits: null,
  // The #346 downgrade clock: present only while the account is over its
  // allowance and inside its 60-day window. Server-written (firestore.rules
  // refuses client writes), and it rides this listener because it lives on the
  // same document — a fourth field, not a fourth socket.
  storageGrace: null,
  error: null,
};

const useBillingSummary = (user) => {
  const [summary, setSummary] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  // Keyed on the uid, not the user object: the effect now writes state on the
  // way in, so depending on a reference a caller may rebuild each render would
  // re-subscribe and re-clear in a loop.
  const uid = user ? user.uid : null;

  useEffect(() => {
    if (!uid) {
      setSummary(EMPTY);
      setLoading(false);
      return;
    }

    // A cold load resolves `user` as null first, so this effect re-runs when
    // auth lands. Without re-raising the flag, `loading` is already false for
    // the whole Firestore round-trip and a consumer reads the empty summary as
    // settled fact — i.e. a subscriber looks signed-out-and-unsubscribed.
    setLoading(true);
    setSummary(EMPTY);

    const userRef = doc(db, 'users', uid);
    const unsubscribe = onSnapshot(userRef, (snapshot) => {
      const data = snapshot.exists() ? snapshot.data() : null;
      setSummary({
        entitlements: evaluateEntitlements(data, Date.now() / 1000),
        subscription: (data && data.subscription) || null,
        credits: (data && data.credits) || null,
        storageGrace: (data && data.storageGrace) || null,
        error: null,
      });
      setLoading(false);
    }, (error) => {
      console.error('Error listening to billing summary:', error);
      // Fall back to Free defaults rather than leaving entitlements null: the
      // consumer's `!entitlements` guard would otherwise render its loading
      // placeholder forever, with nothing telling the customer it had failed.
      setSummary({
        entitlements: evaluateEntitlements(null, Date.now() / 1000),
        subscription: null,
        credits: null,
        storageGrace: null,
        error,
      });
      setLoading(false);
    });

    return () => unsubscribe();
  }, [uid]);

  return { ...summary, loading };
};

export default useBillingSummary;
